/**
 * Permission codes for the store-settings domains.
 *
 * Mirrors the BFF permission model (see the planning doc). Consume with
 * `usePermissions().hasPermission(...)` from `@/shared/permissions` to gate future
 * settings screens and actions. Codes are dot-namespaced under `settings.<domain>.*`
 * and honour the `settings.*` wildcard supported by the permission context.
 */
import type { PermissionCode } from '@/shared/permissions';

export const STORE_SETTINGS_PERMISSIONS = {
  general: {
    view: 'settings.general.view',
    manage: 'settings.general.manage',
  },
  delivery: {
    view: 'settings.delivery.view',
    manage: 'settings.delivery.manage',
    credentials: 'settings.delivery.credentials.manage',
  },
  payments: {
    view: 'settings.payments.view',
    manage: 'settings.payments.manage',
    credentials: 'settings.payments.credentials.manage',
  },
  checkout: {
    view: 'settings.checkout.view',
    manage: 'settings.checkout.manage',
  },
  orders: {
    view: 'settings.orders.view',
    manage: 'settings.orders.manage',
    lifecycle: 'settings.orders.lifecycle.manage',
  },
  pricing: {
    view: 'settings.pricing.view',
    manage: 'settings.pricing.manage',
    credentials: 'settings.pricing.credentials.manage',
  },
  catalog: {
    view: 'settings.catalog.view',
    manage: 'settings.catalog.manage',
  },
  customers: {
    view: 'settings.customers.view',
    manage: 'settings.customers.manage',
  },
  notifications: {
    view: 'settings.notifications.view',
    manage: 'settings.notifications.manage',
    credentials: 'settings.notifications.credentials.manage',
  },
  content: {
    view: 'settings.content.view',
    manage: 'settings.content.manage',
  },
  engagement: {
    view: 'settings.engagement.view',
    manage: 'settings.engagement.manage',
  },
  telegram: {
    view: 'settings.telegram.view',
    manage: 'settings.telegram.manage',
    credentials: 'settings.telegram.credentials.manage',
  },
  max: {
    view: 'settings.max.view',
    manage: 'settings.max.manage',
    credentials: 'settings.max.credentials.manage',
  },
  web: {
    view: 'settings.web.view',
    manage: 'settings.web.manage',
  },
  ozon: {
    view: 'settings.ozon.view',
    manage: 'settings.ozon.manage',
  },
  wildberries: {
    view: 'settings.wildberries.view',
    manage: 'settings.wildberries.manage',
  },
  'yandex-market': {
    view: 'settings.yandex-market.view',
    manage: 'settings.yandex-market.manage',
  },
  lamoda: {
    view: 'settings.lamoda.view',
    manage: 'settings.lamoda.manage',
  },
  // ── Platform services added in product-catalog/docs/obzr-ecom-platform.md ──────
  tenant: {
    view: 'settings.tenant.view',
    manage: 'settings.tenant.manage',
  },
  store: {
    view: 'settings.store.view',
    manage: 'settings.store.manage',
  },
  address: {
    view: 'settings.address.view',
    manage: 'settings.address.manage',
    credentials: 'settings.address.credentials.manage',
  },
  inventory: {
    view: 'settings.inventory.view',
    manage: 'settings.inventory.manage',
  },
  media: {
    view: 'settings.media.view',
    manage: 'settings.media.manage',
  },
  'ai-agents': {
    view: 'settings.ai-agents.view',
    manage: 'settings.ai-agents.manage',
    credentials: 'settings.ai-agents.credentials.manage',
  },
  basket: {
    view: 'settings.basket.view',
    manage: 'settings.basket.manage',
  },
} as const satisfies Record<string, Record<string, PermissionCode>>;

/** Settings domain keys, e.g. `'delivery'`. */
export type StoreSettingsDomain = keyof typeof STORE_SETTINGS_PERMISSIONS;

/** Flat list of every settings permission code (useful for mocks / tests). */
export const ALL_STORE_SETTINGS_PERMISSIONS: PermissionCode[] = Object.values(
  STORE_SETTINGS_PERMISSIONS,
).flatMap((domain) => Object.values(domain));
